/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// redux utils
import { createSlice } from '@reduxjs/toolkit';
// thunks
import {
    fetchUniqueTriviaUsersReport,
    fetchTotalAnswersReport,
    fetchTopCardsReport,
    fetchTopSkippedReport,
    fetchTriviaEngagementRateReport,
    fetchAvgInViewReport
 } from '../thunks/triviapolls';
 import { fetchProductDataLastUpdated } from '../thunks/product';

 const detailsDefaultData = { daily: {}, weekly: {}, monthly: {}, quarterly: {}}

/* ========== ~~~~~~~~~~ TRIVIA & POLLS SLICE : REDUCER ~~~~~~~~~~ ========== */

const initialState = {
    lastUpdated: null,
    totalAnswers: {
        overview: null,
        details: detailsDefaultData,
        status: 'initial',
        isDataReceived: false
    },
    uniqueUsers: {
        status: 'initial',
        isDataReceived: false
    },
    topCards: {
        data: [],
        status: 'initial',
        isDataReceived: false
    },
    topSkippedCards: {
        data: [],
        status: 'initial',
        isDataReceived: false
    },
    uniqueTriviaUsers: {
        overview: null,
        details: detailsDefaultData,
        status: 'initial',
        isDataReceived: false
    },
    triviaEngagementRate: {
      overview: null,
      details: detailsDefaultData,
      status: 'initial',
      isDataReceived: false
    },
    avgInView: {
        overview: null,
        details: detailsDefaultData,
        status: 'initial',
        isDataReceived: false,
    },
};


const mergeUsersEngagementRateData = state  => {
  state.uniqueTriviaUsers.overview.period = {...state.uniqueTriviaUsers.overview.period, ...state.triviaEngagementRate.overview?.period}
  const uniqueTriviaUsersDetails = {...state.uniqueTriviaUsers.details}
  const triviaEngagementRateDetails = {...state.triviaEngagementRate.details}
  Object.keys(uniqueTriviaUsersDetails).forEach(interval => {
      Object.keys(uniqueTriviaUsersDetails[interval]).forEach(date => 
        state.uniqueTriviaUsers.details[interval][date] = {
            ...uniqueTriviaUsersDetails[interval][date], 
            ...triviaEngagementRateDetails[interval][date]
        }
      )
  })  
}

export const triviaAndPollsSlice = createSlice({
    name: 'triviaAndPolls',
    initialState,
    reducers: {

    },
    extraReducers: builder => {
        builder
            // fetch product data last updated timestamp
            .addCase(fetchProductDataLastUpdated.pending, (state, action) => {
                state.lastUpdated = null;
            })
            .addCase(fetchProductDataLastUpdated.fulfilled, (state, action) => {
                const { last_updated } = action.payload
                state.lastUpdated = last_updated;
            })
            .addCase(fetchProductDataLastUpdated.rejected, (state, action) => {
                state.lastUpdated = null;
            })

            // fetch Total Answers Report
            .addCase(fetchTotalAnswersReport.pending, (state, action) => {
                state.totalAnswers.status = 'pending'
                state.totalAnswers.details = detailsDefaultData
            })
            .addCase(fetchTotalAnswersReport.fulfilled, (state, action) => {
                const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
                state.totalAnswers.overview = Overview || state.totalAnswers.overview
                if(Daily) state.totalAnswers.details.daily = Daily
                if(Weekly) state.totalAnswers.details.weekly = Weekly
                if(Monthly) state.totalAnswers.details.monthly = Monthly
                if(Quarterly) state.totalAnswers.details.quarterly = Quarterly  
                state.totalAnswers.status = 'fulfilled'
                state.totalAnswers.isDataReceived = true
            })
            .addCase(fetchTotalAnswersReport.rejected, (state, action) => {
                state.totalAnswers.status = 'rejected'

            })

            // fetch Unique Trivia Users Report
            .addCase(fetchUniqueTriviaUsersReport.pending, (state, action) => {
                state.uniqueTriviaUsers.status = 'pending'
                state.uniqueTriviaUsers.details = detailsDefaultData
            })
            .addCase(fetchUniqueTriviaUsersReport.fulfilled, (state, action) => {
                const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
                state.uniqueTriviaUsers.overview = Overview || state.uniqueTriviaUsers.overview
                state.uniqueTriviaUsers.details = {daily: Daily, weekly: Weekly, monthly: Monthly, quarterly: Quarterly};
                if(state.triviaEngagementRate.status === 'fulfilled') mergeUsersEngagementRateData(state)
                state.uniqueTriviaUsers.status = 'fulfilled'
                state.uniqueTriviaUsers.isDataReceived = true
            })
            .addCase(fetchUniqueTriviaUsersReport.rejected, (state, action) => {
                state.uniqueTriviaUsers.status = 'rejected'
            })

            // fetch Trivia Engagement Rate Report
            .addCase(fetchTriviaEngagementRateReport.pending, (state, action) => {
                state.triviaEngagementRate.status = 'pending'
                state.triviaEngagementRate.details = detailsDefaultData   
            })
            .addCase(fetchTriviaEngagementRateReport.fulfilled, (state, action) => {
                const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
                state.triviaEngagementRate.overview = Overview || state.triviaEngagementRate.overview
                if(Daily) state.triviaEngagementRate.details.daily = Daily
                if(Weekly) state.triviaEngagementRate.details.weekly = Weekly
                if(Monthly) state.triviaEngagementRate.details.monthly = Monthly
                if(Quarterly) state.triviaEngagementRate.details.quarterly = Quarterly
                // if(state.uniqueTriviaUsers.status === 'fulfilled') mergeUsersEngagementRateData(state)
                state.triviaEngagementRate.status = 'fulfilled'
                state.triviaEngagementRate.isDataReceived = true
            })
            .addCase(fetchTriviaEngagementRateReport.rejected, (state, action) => {
                state.triviaEngagementRate.status = 'rejected'
            })

            // fetch Top Cards Report
            .addCase(fetchTopCardsReport.pending, (state, action) => {
                state.topCards.status = 'pending'
            })
            .addCase(fetchTopCardsReport.fulfilled, (state, action) => {
                state.topCards.data = action.payload
                state.topCards.status = 'fulfilled'
                state.topCards.isDataReceived = true
            })
            .addCase(fetchTopCardsReport.rejected, (state, action) => {
                state.topCards.status = 'rejected'
            })

            // fetch Top Skipped Report
            .addCase(fetchTopSkippedReport.pending, (state, action) => {
                state.topSkippedCards.status = 'pending'
            })
            .addCase(fetchTopSkippedReport.fulfilled, (state, action) => {
                state.topSkippedCards.data = action.payload
                state.topSkippedCards.status = 'fulfilled'
                state.topSkippedCards.isDataReceived = true
            })
            .addCase(fetchTopSkippedReport.rejected, (state, action) => {
                state.topSkippedCards.status = 'rejected'
            })

            // fetch Avg In View Data Report
            .addCase(fetchAvgInViewReport.pending, (state, action) => {
                state.avgInView.status = 'pending'
                state.avgInView.details = detailsDefaultData;
            })
            .addCase(fetchAvgInViewReport.fulfilled, (state, action) => {
                  const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
                  state.avgInView.overview = Overview || state.avgInView.overview
                  if(Daily) state.avgInView.details.daily = Daily
                  if(Weekly) state.avgInView.details.weekly = Weekly
                  if(Monthly) state.avgInView.details.monthly = Monthly
                  if(Quarterly) state.avgInView.details.quarterly = Quarterly
                  state.avgInView.status = 'fulfilled'
                  state.avgInView.isDataReceived = true
            })
            .addCase(fetchAvgInViewReport.rejected, (state, action) => {
                  state.avgInView.status = 'rejected'
            })
    }
});

// export const {  } = triviaAndPolls.actions;
export default triviaAndPollsSlice.reducer;
